/**
 * OAuth connect button component.
 * Handles OAuth flow initiation and connection status.
 */

// React Imports
import { useState } from 'react';
import { Button } from '@coordio/ui';
import {
  Link2 as LinkIcon,
  RefreshCw as RefreshIcon,
} from 'lucide-react';
import { connectorService } from '../../services/connectorService';
import { useNotificationStore } from '../../store/notificationStore';
import { ConnectionStatusIndicator, type ConnectionStatus } from './ConnectionStatusIndicator';

export interface OAuthConnectButtonProps {
  instanceId: string;
  connectorName: string;
  isConnected?: boolean;
  onConnected?: () => void;
  onError?: (error: string) => void;
}

/**
 * OAuth connect button component.
 */
export const OAuthConnectButton = ({
  instanceId,
  connectorName,
  isConnected = false,
  onConnected,
  onError,
}: OAuthConnectButtonProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>(isConnected ? 'Connected' : 'Disconnected');

  const handleConnect = async () => {
    setLoading(true);
    try {
      const response = await connectorService.initiateOAuth(instanceId);

      // Open OAuth authorization URL in popup or redirect
      if (response.authorizationUrl) {
        // Store state for callback verification
        sessionStorage.setItem(`oauth_state_${instanceId}`, response.state);

        // Open popup window for OAuth
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          response.authorizationUrl,
          'OAuth Authorization',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        // Listen for OAuth callback (this would typically be handled via a callback route)
        if (popup) {
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              setLoading(false);
              checkConnectionStatus();
            }
          }, 1000);
        }
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to initiate OAuth flow';
      setStatus('Error');
      addNotification(message, 'error');
      onError?.(message);
      setLoading(false);
    }
  };

  const handleReconnect = async () => {
    setStatus('Disconnected');
    await handleConnect();
  };

  const checkConnectionStatus = async () => {
    try {
      const instance = await connectorService.getConnectorInstance(instanceId);
      if (instance.isCredentialValid) {
        setStatus('Connected');
        onConnected?.();
        addNotification(`Successfully connected to ${connectorName}`, 'success');
      } else {
        setStatus('Disconnected');
      }
    } catch (error) {
      setStatus('Error');
    }
  };

  if (isConnected && status === 'Connected') {
    return (
      <div className="flex items-center gap-4">
        <ConnectionStatusIndicator status="Connected" />
        <Button
          variant="outline"
          size="sm"
          onClick={handleReconnect}
        >
          <RefreshIcon size={16} className="mr-2" />
          Reconnect
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Button
        onClick={handleConnect}
        disabled={loading}
        className="w-full"
      >
        {loading ? (
          <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <LinkIcon size={16} className="mr-2" />
        )}
        {loading ? 'Connecting...' : 'Connect'}
      </Button>
      {status === 'Error' && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800/40 dark:bg-red-950/50 dark:text-red-300">
          Connection failed. Please try again.
        </div>
      )}
    </div>
  );
};
